/**
 * digital-detox.js
 * Infinite CSS-driven marquee for the Digital Detox section.
 *
 * Strategy:
 *  1. Wrap original inner + one clone inside a single flex strip.
 *  2. Animate the strip with translateX( -50% ) — seamless loop.
 *  3. Duration scales with original content width for constant speed.
 *  4. Respects prefers-reduced-motion.
 *  5. Pauses the strip on mouseenter / focusin for accessibility.
 *  6. cloneNode(true) copies inline styles → --dd-tag-hover preserved on clones.
 *  7. Re-inits on Shopify Theme Editor section:load / block:select events.
 */

(function () {
  'use strict';

  const SPEED_PX_PER_SEC = 80; // adjust for faster/slower scroll

  function initMarquee(section) {
    const track = section.querySelector('[data-dd-track]');
    const inner = section.querySelector('[data-dd-inner]');
    if (!track || !inner) return;

    // Avoid double-init (e.g. shopify:section:load fires twice)
    if (track.dataset.ddReady) return;
    track.dataset.ddReady = '1';

    // ── Clone original strip ──────────────────────────────────
    // cloneNode(true) copies all attributes including inline style
    // (which carries --dd-tag-hover per tag), so hovers work on clones.
    const clone = inner.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    // Remove shopify block attributes from clones to avoid duplicates in editor
    clone.querySelectorAll('[data-shopify-editor-block]').forEach(function (el) {
      el.removeAttribute('data-shopify-editor-block');
    });
    track.appendChild(clone);

    // ── Measure & set CSS vars ────────────────────────────────
    // scrollWidth = full width of original set (tags + gaps)
    const totalWidth = inner.scrollWidth;
    const duration = totalWidth / SPEED_PX_PER_SEC;

    // The strip moves by exactly one copy width → seamless 50% loop
    track.style.setProperty('--dd-marquee-offset', `-${totalWidth}px`);
    track.style.setProperty('--dd-marquee-duration', `${duration}s`);

    // ── Start animation (respects reduced-motion) ─────────────
    const motionOk = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
    if (motionOk) {
      inner.setAttribute('data-running', '');
      clone.setAttribute('data-running', '');
    }

    // ── Pause / resume on interaction ────────────────────────
    function pause() {
      inner.style.animationPlayState = 'paused';
      clone.style.animationPlayState = 'paused';
    }
    function resume() {
      inner.style.animationPlayState = 'running';
      clone.style.animationPlayState = 'running';
    }

    // Listen on the track container so the strip doesn't restart
    // when the cursor moves between tags.
    track.addEventListener('mouseenter', pause);
    track.addEventListener('mouseleave', resume);
    track.addEventListener('focusin', pause);
    track.addEventListener('focusout', resume);
  }

  function initAnimations(section) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    // Prevent re-initialization
    if (section.dataset.ddAnimReady) return;
    section.dataset.ddAnimReady = '1';

    const header = section.querySelector('.digital-detox__header');
    const mediaRow = section.querySelector('.digital-detox__media-row');
    const stepsCard = section.querySelector('.digital-detox__steps-card');

    if (!window.IntersectionObserver) {
      // Fallback
      if (header) header.classList.add('is-visible');
      if (mediaRow) mediaRow.classList.add('is-visible');
      if (stepsCard) stepsCard.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: "0px",
      threshold: 0.15 // Triggers when 15% of the element is visible
    });

    if (header) observer.observe(header);
    if (mediaRow) observer.observe(mediaRow);
    if (stepsCard) observer.observe(stepsCard);
  }

  function initDdCtaSmoothScroll() {
    var ctas = document.querySelectorAll('.digital-detox__cta');
    if (!ctas.length) return;

    function getHeaderOffset() {
      var header = document.querySelector('#header-component, .header, header[role="banner"]');
      if (!header) return 0;
      var rect = header.getBoundingClientRect();
      var style = window.getComputedStyle(header);
      if (style.position === 'fixed' || style.position === 'sticky') {
        return rect.height;
      }
      return 0;
    }

    function targetTop(target) {
      return target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset() - 16;
    }

    function correctIfDrifted(target, attempts) {
      attempts = attempts || 0;
      if (attempts > 4) return;

      var desired = targetTop(target);
      var current = window.pageYOffset;

      if (Math.abs(desired - current) > 2) {
        window.scrollTo({ top: desired, behavior: 'smooth' });
        setTimeout(function () { correctIfDrifted(target, attempts + 1); }, 350);
      }
    }

    function waitForScrollEnd(cb) {
      var lastY = window.pageYOffset;
      var stable = 0;
      var ticks = 0;

      function tick() {
        ticks++;
        var y = window.pageYOffset;
        if (Math.abs(y - lastY) < 0.5) {
          stable++;
          if (stable >= 4) return cb();
        } else {
          stable = 0;
        }
        lastY = y;
        if (ticks > 180) return cb();
        requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }

    ctas.forEach(function (cta) {
      if (cta.dataset.ddSmoothScrollReady) return;
      cta.dataset.ddSmoothScrollReady = '1';

      var href = cta.getAttribute('href') || '';
      if (href.charAt(0) !== '#' || href.length < 2) return;

      cta.addEventListener('click', function (e) {
        var id = href.slice(1);
        var target = document.getElementById(id);
        if (!target) return;

        e.preventDefault();

        if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.refresh) {
          ScrollTrigger.refresh();
        }

        window.scrollTo({ top: targetTop(target), behavior: 'smooth' });

        if (history.pushState) {
          history.pushState(null, '', href);
        }

        waitForScrollEnd(function () {
          if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.refresh) {
            ScrollTrigger.refresh();
          }
          correctIfDrifted(target);
        });
      });
    });
  }

  function init() {
    document.querySelectorAll('.digital-detox').forEach(function (section) {
      initMarquee(section);
      initAnimations(section);
    });
    initDdCtaSmoothScroll();
  }

  // ── Bootstrap ────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor events
  document.addEventListener('shopify:section:load', function (e) {
    const section = e.target.querySelector('.digital-detox');
    if (section) {
      // Reset ready flag so re-init works cleanly after editor reload
      const track = section.querySelector('[data-dd-track]');
      if (track) delete track.dataset.ddReady;
      initMarquee(section);
    }
  });

  // When a block is selected in the editor the marquee should still work
  document.addEventListener('shopify:block:select', function (e) {
    const section = e.target.closest('.digital-detox');
    if (section) initMarquee(section);
  });
})();
